import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import type {
  VendorIntegrationListResponse,
  VendorIntegrationWithCodeResponse,
} from "@pull/api-contract";
import type { VendorIntegrationType } from "@pull/shared";
import { createApiClient } from "../api/ApiClient";

const INTEGRATIONS_KEY = ["dashboard-integrations"];

export function useIntegrations() {
  const { getToken } = useAuth();
  const api = createApiClient(getToken);

  return useQuery({
    queryKey: INTEGRATIONS_KEY,
    queryFn: () => api.get<VendorIntegrationListResponse>("/vendors/dashboard/integrations"),
  });
}

/** Creates the vendor's Wix integration. The response holds the only copy of its code. */
export function useCreateWixIntegration() {
  const { getToken } = useAuth();
  const api = createApiClient(getToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<VendorIntegrationWithCodeResponse>("/vendors/dashboard/integrations/wix", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_KEY });
    },
  });
}

/** Issues a replacement code, which retires the previous one. */
export function useReissueIntegrationCode() {
  const { getToken } = useAuth();
  const api = createApiClient(getToken);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (type: VendorIntegrationType) =>
      api.post<VendorIntegrationWithCodeResponse>(
        `/vendors/dashboard/integrations/${type.toLowerCase()}/code`,
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_KEY });
    },
  });
}
